import { redirect } from "@tanstack/react-router";
import { can } from "@/lib/staff-roles";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Banknote, ChevronDown, ChevronRight, Mail } from "lucide-react";
import { BookingChargesPanel } from "@/components/admin/BookingChargesPanel";
import {
  listStayBilling,
  recordStayPayment,
  sendStayPaymentRequest,
  type StayRow,
} from "@/lib/stay-billing.functions";

export const Route = createFileRoute("/_authenticated/admin/stays")({
  beforeLoad: ({ context }) => {
    if (!can(context.staffRole, "viewMoney")) {
      throw redirect({ to: "/admin/calendar" });
    }
  },
  head: () => ({
    meta: [
      { title: "Guest Stays & Billing — Coffman Cove Lodging" },
      {
        name: "description",
        content: "Upcoming and past guest stays with charges and payment requests.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminStays,
});

const money = (cents: number) =>
  (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });

function prettyDay(iso: string | null) {
  if (!iso) return "—";
  return new Date(`${iso.slice(0, 10)}T00:00:00Z`).toLocaleDateString("en-US", {
    timeZone: "UTC",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function AdminStays() {
  const [tab, setTab] = useState<"upcoming" | "past">("upcoming");
  const [open, setOpen] = useState<string | null>(null);
  const qc = useQueryClient();

  const load = useServerFn(listStayBilling);
  const request = useServerFn(sendStayPaymentRequest);
  const record = useServerFn(recordStayPayment);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-stay-billing"],
    queryFn: () => load(),
  });

  const refresh = () => void qc.invalidateQueries({ queryKey: ["admin-stay-billing"] });

  const requestMutation = useMutation({
    mutationFn: request,
    onSuccess: (result) => {
      if (result.ok) toast.success("Payment request emailed to the guest.");
      else if (result.reason === "nothing_due") toast.error("Nothing is outstanding on this stay.");
      else if (result.reason === "no_email") toast.error("This stay has no email on file.");
      else toast.error("That email address can't receive mail from us.");
      refresh();
    },
    onError: () => toast.error("Could not send the payment request."),
  });

  const recordMutation = useMutation({
    mutationFn: record,
    onSuccess: (result) => {
      if (result.ok) toast.success("Payment recorded.");
      else toast.error("There's no balance on file for this stay yet.");
      refresh();
    },
    onError: () => toast.error("Could not record that payment."),
  });

  const today = new Date().toISOString().slice(0, 10);
  const stays = (data?.stays ?? []) as StayRow[];

  const { upcoming, past } = useMemo(() => {
    const up: StayRow[] = [];
    const old: StayRow[] = [];
    for (const s of stays) {
      const end = (s.departure_date ?? s.arrival_date ?? "").slice(0, 10);
      if (!end || end >= today) up.push(s);
      else old.push(s);
    }
    old.reverse();
    return { upcoming: up, past: old };
  }, [stays, today]);

  const list = tab === "upcoming" ? upcoming : past;
  const outstanding = upcoming.reduce((sum, s) => sum + s.balance_cents, 0);

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <header>
        <h1 className="font-display text-2xl">Guest stays &amp; billing</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Every confirmed stay, what each party owes, and how to collect it.
        </p>
        <p className="mt-2 text-sm">
          Outstanding on upcoming stays: <strong>{money(outstanding)}</strong>
        </p>
      </header>

      <div className="mt-5 flex gap-2">
        {(["upcoming", "past"] as const).map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={
              tab === key
                ? "rounded-full bg-[var(--color-ember)] px-4 py-2 text-sm font-semibold text-[var(--color-ember-foreground)]"
                : "rounded-full border border-border px-4 py-2 text-sm text-muted-foreground"
            }
          >
            {key === "upcoming" ? `Upcoming (${upcoming.length})` : `Past (${past.length})`}
          </button>
        ))}
      </div>

      {isLoading ? <p className="mt-6 text-sm text-muted-foreground">Loading stays…</p> : null}
      {error ? <p className="mt-6 text-sm text-destructive">Could not load stays.</p> : null}
      {!isLoading && !error && list.length === 0 ? (
        <p className="mt-6 text-sm text-muted-foreground">No {tab} stays.</p>
      ) : null}

      <div className="mt-5 space-y-3">
        {list.map((stay) => {
          const expanded = open === stay.id;
          const paid = (stay.order_status ?? "").toLowerCase() === "paid";
          return (
            <section key={stay.id} className="rounded-xl border border-border bg-card">
              <button
                type="button"
                onClick={() => setOpen(expanded ? null : stay.id)}
                className="flex w-full flex-wrap items-center justify-between gap-3 px-4 py-4 text-left"
              >
                <span className="flex items-center gap-2">
                  {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  <span>
                    <span className="font-semibold">{stay.name}</span>
                    <span className="block text-xs text-muted-foreground">
                      {prettyDay(stay.arrival_date)} – {prettyDay(stay.departure_date)} ·{" "}
                      {stay.party_size ?? "?"} guests
                      {stay.confirmation_code ? ` · ${stay.confirmation_code}` : ""}
                    </span>
                  </span>
                </span>
                <span className="text-right">
                  <span className="block font-semibold">
                    {paid ? "Paid" : money(stay.balance_cents)}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {paid ? "settled" : "balance due"}
                  </span>
                </span>
              </button>

              {expanded ? (
                <div className="border-t border-border px-4 pb-5 pt-4">
                  <p className="text-sm text-muted-foreground">
                    {stay.email}
                    {stay.phone ? ` · ${stay.phone}` : ""}
                  </p>

                  {stay.order_items.length > 0 ? (
                    <ul className="mt-3 divide-y divide-border text-sm">
                      {stay.order_items.map((item, i) => (
                        <li key={i} className="flex justify-between py-1.5">
                          <span>
                            {item.name} × {item.qty ?? 1}
                          </span>
                          <span>
                            {money(Number(item.qty ?? 1) * Number(item.unit_price_cents ?? 0))}
                          </span>
                        </li>
                      ))}
                      <li className="flex justify-between py-1.5 font-semibold">
                        <span>Stay subtotal</span>
                        <span>{money(stay.order_total_cents)}</span>
                      </li>
                    </ul>
                  ) : (
                    <p className="mt-3 text-sm text-muted-foreground">
                      No stay charges on file yet.
                    </p>
                  )}

                  <BookingChargesPanel bookingId={stay.id} />

                  <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-border pt-4">
                    <button
                      type="button"
                      disabled={requestMutation.isPending || paid}
                      onClick={() =>
                        requestMutation.mutate({ data: { bookingId: stay.id, message: null } })
                      }
                      className="inline-flex items-center gap-2 rounded-full bg-[var(--color-ember)] px-4 py-2 text-sm font-semibold text-[var(--color-ember-foreground)] disabled:opacity-50"
                    >
                      <Mail size={14} aria-hidden />
                      Email payment request
                    </button>
                    <ManualPayment
                      disabled={recordMutation.isPending || paid}
                      defaultAmount={stay.balance_cents}
                      onSubmit={(method, amount_cents, reference) =>
                        recordMutation.mutate({
                          data: { bookingId: stay.id, method, amount_cents, reference },
                        })
                      }
                    />
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    The email links the guest to their own page, where they pay by card or bank
                    transfer. Check and cash are recorded here.
                  </p>
                </div>
              ) : null}
            </section>
          );
        })}
      </div>
    </div>
  );
}

function ManualPayment({
  disabled,
  defaultAmount,
  onSubmit,
}: {
  disabled: boolean;
  defaultAmount: number;
  onSubmit: (
    method: "check" | "cash" | "ach",
    amountCents: number,
    reference: string | null,
  ) => void;
}) {
  const [method, setMethod] = useState<"check" | "cash" | "ach">("check");
  const [amount, setAmount] = useState(() => (defaultAmount / 100).toFixed(2));
  const [reference, setReference] = useState("");

  return (
    <form
      className="flex flex-wrap items-center gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        const dollars = Number(amount);
        if (!Number.isFinite(dollars) || dollars <= 0) {
          toast.error("Enter the amount received.");
          return;
        }
        onSubmit(method, Math.round(dollars * 100), reference.trim() || null);
      }}
    >
      <select
        value={method}
        onChange={(e) => setMethod(e.target.value as "check" | "cash" | "ach")}
        className="rounded-md border border-border bg-background px-3 py-2 text-sm"
        aria-label="Payment method"
      >
        <option value="check">Check</option>
        <option value="cash">Cash</option>
        <option value="ach">Bank transfer</option>
      </select>
      <input
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        inputMode="decimal"
        aria-label="Amount received"
        className="w-28 rounded-md border border-border bg-background px-3 py-2 text-sm"
      />
      <input
        value={reference}
        onChange={(e) => setReference(e.target.value)}
        placeholder="Check # / note"
        aria-label="Reference"
        className="w-40 rounded-md border border-border bg-background px-3 py-2 text-sm"
      />
      <button
        type="submit"
        disabled={disabled}
        className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-semibold disabled:opacity-50"
      >
        <Banknote size={14} aria-hidden />
        Record payment
      </button>
    </form>
  );
}

export default AdminStays;
